import { useState, useMemo, useContext } from 'react';
import { ValidationContext } from '@/components/validation';
import type { ValidationError } from '@/components/validation';

interface UseClientFormValidationProps {
  currentStep: number;
  isLoading: boolean;
  isLoadingDefaults: boolean;
  isEditing: boolean;
}

export const useClientFormValidation = ({
  currentStep,
  isLoading,
  isLoadingDefaults,
  isEditing,
}: UseClientFormValidationProps) => {
  const ctx = useContext(ValidationContext);
  if (!ctx) throw new Error('useClientFormValidation must be used within ValidationProvider');

  const [isRolesStepValid, setIsRolesStepValid] = useState(true);
  const [isMultipliersStepValid, setIsMultipliersStepValid] = useState(true);

  const stepScope = String(currentStep);

  // Determine if Next button should be disabled
  const isNextButtonDisabled = useMemo(() => {
    if (isLoading || (isLoadingDefaults && !isEditing)) {
      return true;
    }

    const schemaValid = ctx.isValid(stepScope);

    switch (currentStep) {
      case 1:
        return !isRolesStepValid || !schemaValid;
      case 2:
        return !isMultipliersStepValid || !schemaValid;
      default:
        return !schemaValid;
    }
  }, [
    isLoading,
    isLoadingDefaults,
    isEditing,
    currentStep,
    isRolesStepValid,
    isMultipliersStepValid,
    stepScope,
    ctx,
  ]);

  const isNextButtonLoading = useMemo(
    () => isLoading || (isLoadingDefaults && !isEditing),
    [isLoading, isLoadingDefaults, isEditing],
  );

  /**
   * Bridge: derive `errors` and `touchedFields` from ValidationContext for
   * child components that still consume the legacy prop-based API.
   *
   * `bridgeErrors` contains ALL active validation errors (across all scopes).
   * `bridgeTouchedFields` contains the field keys for which `getFieldError`
   * returns a non-null value (i.e. the field has been blurred AND has an error).
   *
   * Because `getErrorMessage(field, errors, touched)` returns the message only
   * when `touched.has(field)`, and the context's `getFieldError` already gates
   * on blur status, we build `bridgeTouchedFields` from the subset of error
   * fields that are currently visible.
   */
  const bridgeErrors: ValidationError[] = ctx.getActiveErrors();
  const bridgeTouchedFields = useMemo(() => {
    const touched = new Set<string>();
    for (const error of bridgeErrors) {
      if (ctx.getFieldError(error.field) !== null) {
        touched.add(error.field);
      }
    }
    return touched;
  }, [bridgeErrors, ctx]);

  return {
    isRolesStepValid,
    setIsRolesStepValid,
    isMultipliersStepValid,
    setIsMultipliersStepValid,
    isNextButtonDisabled,
    isNextButtonLoading,
    bridgeErrors,
    bridgeTouchedFields,
    ctx,
  };
};
